import type { ReactNode } from "react";

import type { ConductorRowResponse, EarthingResponse, ElectrodeResponse } from "../services/earthing";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import {
  AREA_ORIGIN_LABELS,
  DISCONNECTION_TIME_SOURCE_LABELS,
  ELECTRODE_KIND_LABELS,
  ENERGY_BASIS_LABELS,
  FAULT_CURRENT_ORIGIN_LABELS,
  K_ORIGIN_LABELS,
  MATERIAL_LABELS,
  ROLE_LABELS,
  SEASONAL_FACTOR_ORIGIN_LABELS,
  SYSTEM_KIND_LABELS,
  TARGET_ORIGIN_LABELS,
} from "./earthingLabels";
import { RATIO_PASS, RATIO_WORDS, STATUS_LABELS, statusHeading } from "./EarthingResultSummary";
import { StatusIcon } from "./StatusIcon";
import { checkTone } from "./statusTone";

interface EarthingResultPanelProps {
  result: EarthingResponse;
}

/** Carried by every result (D2, D7, D12): what the check is and what it is not. */
export const METHOD_NOTE =
  "The protective conductor check is the adiabatic formula of IS 3043:2018 clause 17.2.2.1 as " +
  "printed (I / S = k / √t, stated for disconnection times not exceeding 5 s), solved for the " +
  "required area and compared with the declared area; the current and the time come from the " +
  "linked stored runs or the declarations, k from a declared figure or the held table the " +
  "engineer names. The electrode resistance is the printed formula of IS 3043:2018 clauses " +
  "14.2.1 to 14.2.3 for a uniform soil at the design resistivity; electrodes in parallel are " +
  "divided by the count times the declared utilisation factor, the groups combined in ideal " +
  "parallel. The step and touch voltages, the lightning earthing, the loop and bonding " +
  "continuity, the corrosion allowance, the electrode current loading, the minimum dimensions, " +
  "the selection table, the bonding conductors and the joints' temperatures are not checked " +
  "here; the sheet says so on every study. No conductor size and no electrode is selected: the " +
  "selection stays the engineer's.";

export const RESULT_WORDS = {
  met: "Met",
  notMet: "Not met",
  notCompared: "Not compared",
} as const;

/** The caption of every ratio printed: demand / capacity, at or below 1 passes (follow-up 41). */
export const RATIO_CAPTION = `${RATIO_WORDS} (${RATIO_PASS})`;

function resultKey(met: boolean | null): string {
  if (met === null) {
    return "not-evaluated";
  }
  return met ? "pass" : "fail";
}

function resultWord(met: boolean | null): string {
  if (met === null) {
    return RESULT_WORDS.notCompared;
  }
  return met ? RESULT_WORDS.met : RESULT_WORDS.notMet;
}

// One cell holding an engineering figure: four significant figures with the unit on screen,
// the exact value in the tooltip (display rule A12); an em dash for none.
function Quantity({ value, unit }: { value: string | null | undefined; unit?: string }) {
  const quantity = formatQuantity(value ?? null, unit);
  return <td title={quantity.exact ?? undefined}>{quantity.display}</td>;
}

function Verdict({ met }: { met: boolean | null }) {
  const key = resultKey(met);
  return (
    <td data-check-result={key}>
      <span data-check-pill>
        <StatusIcon tone={checkTone(key)} size={16} />
        {resultWord(met)}
      </span>
    </td>
  );
}

function StatusPill({ status }: { status: EarthingResponse["status"] }) {
  const key = status === "DESIGN_CHECK_PASSED" ? "pass" : status === "NO_SOLUTION" ? "fail" : "review";
  return (
    <td data-check-result={key}>
      <span data-check-pill>
        <StatusIcon tone={checkTone(key)} size={16} />
        {STATUS_LABELS[status]}
      </span>
    </td>
  );
}

function ValueTable({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title}>
      <h3>{title}</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Parameter</th>
              <th scope="col">Value</th>
              <th scope="col">Basis</th>
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </section>
  );
}

function sourceOr(source: string | null | undefined): string {
  return source ?? "source not declared";
}

// The arrangement as declared: the bus, the system earthing, the soil and the target.
function ArrangementTable({ result }: { result: EarthingResponse }) {
  const soil = result.soil;
  return (
    <ValueTable title="Earthing arrangement">
      <tr data-row-key="bus">
        <th scope="row">Bus · system earthing</th>
        <td data-system-kind={result.system_kind}>
          {result.bus_code} · {SYSTEM_KIND_LABELS[result.system_kind]}
        </td>
        <td>{result.sld_bus_code === null ? "no SLD bus named" : `SLD bus ${result.sld_bus_code}`} · declared, information only</td>
      </tr>
      <tr data-row-key="system_voltage_v">
        <th scope="row">System voltage (V)</th>
        <Quantity value={result.system_voltage_v.value} unit="V" />
        <td>{sourceOr(result.system_voltage_v.source)}</td>
      </tr>
      <tr data-row-key="soil">
        <th scope="row">Soil resistivity measured (Ω·m)</th>
        <Quantity value={soil?.resistivity_ohm_m ?? null} unit="Ω·m" />
        <td>
          {soil === null
            ? "not declared"
            : [soil.test_method, soil.test_date, soil.source].filter((item) => item !== null).join(" · ") ||
              "test record not declared"}
        </td>
      </tr>
      <tr data-row-key="design_resistivity_ohm_m">
        <th scope="row">Design resistivity (Ω·m)</th>
        <Quantity value={result.design_resistivity_ohm_m} unit="Ω·m" />
        <td data-seasonal-factor-origin={result.seasonal_factor_origin}>
          {SEASONAL_FACTOR_ORIGIN_LABELS[result.seasonal_factor_origin]}
          {soil?.seasonal_factor ? ` ${soil.seasonal_factor.value} · ${sourceOr(soil.seasonal_factor.source)}` : ""}
        </td>
      </tr>
      <tr data-row-key="target_resistance_ohm">
        <th scope="row">Target resistance (Ω)</th>
        <Quantity value={result.target_resistance_ohm?.value ?? null} unit="Ω" />
        <td data-target-origin={result.target_origin}>
          {result.target_resistance_ohm === null
            ? TARGET_ORIGIN_LABELS[result.target_origin]
            : sourceOr(result.target_resistance_ohm.source)}
        </td>
      </tr>
    </ValueTable>
  );
}

function linkWords(row: ConductorRowResponse): string {
  const words: string[] = [];
  if (row.disconnection_row !== null) {
    const link = row.disconnection_row;
    words.push(`${link.study_code} rev ${link.revision_number} row ${link.row_code}`);
  }
  if (row.linked_fault_run !== null) {
    const run = row.linked_fault_run;
    words.push(`${run.study_code} rev ${run.revision_number} · bus ${run.fault_bus_code} · ${run.calculation_case.toLowerCase()} earth fault`);
  }
  return words.length === 0 ? "no stored run linked" : words.join(" · ");
}

// One line per protective conductor: the current, the time, k, the required and declared areas.
function ConductorsTable({ rows }: { rows: readonly ConductorRowResponse[] }) {
  return (
    <section aria-label="Protective conductors">
      <h3>Protective conductors</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Conductor</th>
              <th scope="col">Role</th>
              <th scope="col">I (A)</th>
              <th scope="col">t (s)</th>
              <th scope="col">I²t (A²s)</th>
              <th scope="col">k</th>
              <th scope="col">Required (mm²)</th>
              <th scope="col">Declared (mm²)</th>
              <th scope="col">Margin (mm²)</th>
              <th scope="col" title={`required / declared: ${RATIO_CAPTION}`}>
                Required / declared ({RATIO_PASS})
              </th>
              <th scope="col">Thermal check</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.code} data-conductor={row.code} data-row-status={row.status}>
                <th scope="row" title={`${row.name} · ${linkWords(row)}`}>
                  {row.code}
                </th>
                <td data-role={row.role}>{ROLE_LABELS[row.role]}</td>
                <td title={row.fault_current_a === null ? undefined : `${row.fault_current_a} A · ${FAULT_CURRENT_ORIGIN_LABELS[row.fault_current_origin]}`}>
                  {formatQuantity(row.fault_current_a, "A").display}
                </td>
                <td title={row.disconnection_time_s === null ? undefined : `${row.disconnection_time_s} s · ${DISCONNECTION_TIME_SOURCE_LABELS[row.disconnection_time_source]}`}>
                  {formatQuantity(row.disconnection_time_s, "s").display}
                </td>
                <td title={row.let_through_energy_a2s === null ? undefined : `${row.let_through_energy_a2s} A²s · ${ENERGY_BASIS_LABELS[row.energy_basis]}`}>
                  {formatQuantity(row.let_through_energy_a2s, "A²s").display}
                </td>
                <td title={row.k_factor === null ? undefined : `${row.k_factor} · ${row.k_locator ?? K_ORIGIN_LABELS[row.k_origin]}${row.k_boundary_conditions ? ` · ${row.k_boundary_conditions}` : ""}`}>
                  {row.k_factor === null ? EMPTY_QUANTITY : `${formatQuantity(row.k_factor).display} (${K_ORIGIN_LABELS[row.k_origin]})`}
                </td>
                <Quantity value={row.required_area_mm2} unit="mm²" />
                <td title={row.declared_area_mm2 === null ? undefined : `${row.declared_area_mm2} mm² · ${AREA_ORIGIN_LABELS[row.area_origin]} · ${sourceOr(row.declared_area_source)}`}>
                  {row.declared_area_mm2 === null
                    ? "not established"
                    : `${formatQuantity(row.declared_area_mm2, "mm²").display}${row.conductor_material === null ? "" : ` ${MATERIAL_LABELS[row.conductor_material].toLowerCase()}`}`}
                </td>
                <Quantity value={row.margin_mm2} unit="mm²" />
                <Quantity value={row.ratio} />
                <Verdict met={row.met} />
                <StatusPill status={row.status} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function dimensionWords(item: ElectrodeResponse): string {
  return Object.entries(item.dimensions)
    .map(([name, value]) => {
      const unit = name.endsWith("_mm") ? "mm" : "m";
      return `${name.replace(/_(m|mm)$/, "").replaceAll("_", " ")} ${formatQuantity(value, unit).display}`;
    })
    .join(" · ");
}

// One line per electrode: the kind and dimensions, the single resistance, the group.
function ElectrodesTable({ items }: { items: readonly ElectrodeResponse[] }) {
  return (
    <section aria-label="Earth electrodes">
      <h3>Earth electrodes</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Electrode</th>
              <th scope="col">Kind · dimensions</th>
              <th scope="col">In parallel · factor</th>
              <th scope="col">Single R (Ω)</th>
              <th scope="col">Group R (Ω)</th>
              <th scope="col">Formula</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.code} data-electrode={item.code} data-electrode-status={item.status}>
                <th scope="row" title={item.material ?? undefined}>
                  {item.code}
                </th>
                <td data-electrode-kind={item.kind} title={sourceOr(item.dimension_source)}>
                  {ELECTRODE_KIND_LABELS[item.kind]} · {dimensionWords(item)}
                </td>
                <td title={item.parallel_factor === null ? undefined : `${item.parallel_factor.value} · ${sourceOr(item.parallel_factor.source)}${item.spacing_m === null ? "" : ` · spacing ${item.spacing_m} m`}`}>
                  {item.count_in_parallel}
                  {item.count_in_parallel > 1
                    ? item.parallel_factor === null
                      ? " · factor not declared"
                      : ` · F ${formatQuantity(item.parallel_factor.value).display}`
                    : ""}
                </td>
                <Quantity value={item.single_resistance_ohm} unit="Ω" />
                <Quantity value={item.group_resistance_ohm} unit="Ω" />
                <td>{item.formula_locator ?? "not computed"}</td>
                <StatusPill status={item.status} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// The arrangement's resistance against the target (3.2).
function ResistanceTable({ result }: { result: EarthingResponse }) {
  return (
    <ValueTable title="Electrode resistance">
      <tr data-row-key="arrangement_resistance_ohm">
        <th scope="row">Arrangement resistance (Ω)</th>
        <Quantity value={result.arrangement_resistance_ohm} unit="Ω" />
        <td>
          {result.arrangement_resistance_ohm === null
            ? "not formed (a soil resistivity or a utilisation factor is missing)"
            : result.electrodes.length > 1
              ? "the groups in ideal parallel, no mutual screening between the groups"
              : "the one group"}
        </td>
      </tr>
      <tr data-row-key="resistance_margin">
        <th scope="row">Margin (Ω) · R / target</th>
        <td title={`${result.resistance_margin_ohm ?? "—"} · ${result.resistance_ratio ?? "—"}`}>
          {formatQuantity(result.resistance_margin_ohm).display} · {formatQuantity(result.resistance_ratio).display}
        </td>
        <td>target − R · R / target: {RATIO_CAPTION}</td>
      </tr>
      <tr data-row-key="resistance_result">
        <th scope="row">Resistance condition</th>
        <Verdict met={result.resistance_met} />
        <td>{result.resistance_met === null ? "no target or no arrangement: nothing compared" : "R ≤ target"}</td>
      </tr>
    </ValueTable>
  );
}

export function EarthingResultPanel({ result }: EarthingResultPanelProps) {
  return (
    <article aria-label="Earthing result">
      <header>
        <h2>Earthing result</h2>
        <p>
          Study code: <strong data-study-code="true">{result.code}</strong>
          {" - "}
          {result.name}
        </p>
        <p>
          Overall status: <strong data-result-status={result.status}>{statusHeading(result)}</strong>
        </p>
      </header>

      <ArrangementTable result={result} />
      {result.conductors.length > 0 ? <ConductorsTable rows={result.conductors} /> : null}
      {result.electrodes.length > 0 ? <ElectrodesTable items={result.electrodes} /> : null}
      {result.electrodes.length > 0 ? <ResistanceTable result={result} /> : null}

      <section aria-label="Jurisdiction profile">
        <h3>Jurisdiction profile</h3>
        <p data-jurisdiction-profile={result.jurisdiction_profile}>
          Designed under the {result.jurisdiction_profile} profile. The k of a named table case is
          the value printed in the held IS 3043:2018 Table 11A to 11D or CPWD Part I 2023 App F
          Tables F-6 / F-7 (controlled engineering data, IR-008); every other figure is the
          engineer's declared value with its source or a stored run's. The arithmetic is the
          standard's printed formulas.
        </p>
      </section>

      <footer>
        <p>
          Calculation results are engineering design checks, not a statutory compliance
          declaration. Confirm the governing project basis, the soil test, the device data and
          every declared figure and its source before approval or issue. {METHOD_NOTE}
        </p>
      </footer>
    </article>
  );
}

export default EarthingResultPanel;
